/**
 * Shared Project Detail Page
 * Handles single project view with PDF viewer, invites management, and project controls
 * URL: /pages/share?id={shareProjectId}
 */

class SharedProjectDetail {
    constructor() {
        this.showLoader(true);
        // Get config from window
        this.config = window.shareProjectDetailConfig || {};
        this.apiBaseUrl = window.API_BASE_URL || '';
        this.currency = this.config.currency;
        this.region = this.config.region;
        this.culture = this.config.culture;
        this.customerEmail = this.config.customerEmail;
        this.customerId = this.config.customerId;
        this.pdfJsScriptUrl = this.config.pdfJsScriptUrl || 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
        this.pdfJsWorkerUrl = this.config.pdfJsWorkerUrl || 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

        // UI Elements
        this.projectHeader = document.getElementById('projectHeader');
        this.heading = document.getElementById('heading');
        this.projectGreeting = document.getElementById('projectGreeting');
        this.projectViewer = document.getElementById('projectViewer');
        this.deleteProject = document.getElementById('deleteProject');
        this.projectNotAvailable = document.getElementById('projectNotAvailable');
        this.serverError = document.getElementById('serverError');
        this.bookProcessing = document.getElementById('bookProcessing');
        this.bookCreationError = document.getElementById('bookCreationError');
        this.loader = document.getElementById('loader');

        // Buttons and inputs
        this.editGreetingBtn = document.getElementById('editGreetingBtn');
        this.addGreetingBtnDesc = document.getElementById('addGreetingBtnDesc');
        this.editGreetingBtnDesc = document.getElementById('editGreetingBtnDesc');
        this.submitGreeting = document.getElementById('submitGreeting');
        this.submitInvitee = document.getElementById('submitInvitee');
        this.addInviteeBtn = document.getElementById('addInviteeBtn');
        this.orderButton = document.getElementById('orderButton');
        this.removeAccessBtn = document.getElementById('removeAccessBtn');
        this.deletePageBtn = document.getElementById('deletePageBtn');

        // Text and display elements
        this.charCount = document.getElementById('charCount');
        this.greetingDesc = document.getElementById('greetingDesc');
        this.emailCount = document.getElementById('emailCount');
        this.emailContainer = document.getElementById('emailContainer');
        this.productDetails = document.getElementById('productDetails');
        this.invitationDetails = document.getElementById('invitationDetails');
        this.inviteList = document.getElementById('inviteList');
        this.inviteTable = document.getElementById('inviteTable');

        // PDF Elements
        this.viewer = document.getElementById('viewer');
        this.viewerContainer = document.getElementById('viewer-container');
        this.fullScreenButton = document.getElementById('fullscreen');
        this.fullScreenButtonLabel = this.fullScreenButton ? this.fullScreenButton.querySelector('.primary-button') : null;
        this.nextPageButton = document.getElementById('nextPage');
        this.prevPageButton = document.getElementById('prevPage');
        this.pageNumberSpan = document.getElementById('pageNumber');
        this.totalPages = document.getElementById('totalPages');
        this.pageIndicator = document.getElementById('pageIndicator');
        this.topHeaderControls = document.getElementById('controls');
        this.pdfLoader = document.getElementById('pdfLoader');

        // Modals
        this.modals = Array.from(document.querySelectorAll('.share-container .modal'));

        // State
        this.project = null;
        this.isInviter = false;
        this.shareProjectId = this.getUrlParam('id');
        this.inviteeList = [];
        this.descriptionCharLimit = 500;
        this.emailLimitValue = 50;
        this.isFullScreen = false;
        this.isMobile = /Mobi/i.test(navigator.userAgent);
        this.pdfDocument = null;
        this.currentPageNumber = 1;
        this.pageCounter = 1;
        this.pdfLayoutMode = 'single';
        this.pdfCanvasContainer = null;
        this.activeRenderTasks = [];
        this.facingBasePageSize = null;
        this.resizeRenderTimeout = null;
        this.pdfLoadTimeout = null;
        this.emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
        this.mimeoUserId = '';
        this.initializeAuth();
    }

    async initializeAuth() {
        if (window.authState && typeof window.authState.ensureSessionOrRedirect === 'function') {
            const hasSession = await window.authState.ensureSessionOrRedirect('missing-token');
            if (!hasSession) {
                this.showLoader(false);
                return;
            }
        }

        const authToken = await this.resolveAuthToken();
        if (authToken) {
            this.mimeoUserId = window.SharedProjectsService.getMimeoUserIdFromAuthToken(authToken);
            this.init();
            this.showLoader(false);
            return;
        }

        this.showLoader(false);
        this.handleAuthFailure('missing-token');
    }

    async resolveAuthToken() {
        if (window.authState && typeof window.authState.getToken === 'function') {
            try {
                return await window.authState.getToken();
            } catch (_error) {
                return null;
            }
        }

        return localStorage.getItem('auth_token');
    }

    init() {
        this.registerEventListeners();
        this.initializeModals();
        this.loadProject();
    }

    registerEventListeners() {
        // PDF navigation
        if (this.fullScreenButton) {
            this.fullScreenButton.addEventListener('click', this.handleFullScreen.bind(this));
        }
        if (this.nextPageButton) {
            this.nextPageButton.addEventListener('click', this.navigateToNextPage.bind(this));
        }
        if (this.prevPageButton) {
            this.prevPageButton.addEventListener('click', this.navigateToPreviousPage.bind(this));
        }

        // Project actions
        if (this.orderButton) {
            this.orderButton.addEventListener('click', this.handleCreateOrder.bind(this));
        }
        if (this.editGreetingBtn) {
            this.editGreetingBtn.addEventListener('click', this.openGreetingModal.bind(this));
        }
        if (this.addGreetingBtnDesc) {
            this.addGreetingBtnDesc.addEventListener('click', this.openGreetingModal.bind(this));
        }
        if (this.editGreetingBtnDesc) {
            this.editGreetingBtnDesc.addEventListener('click', this.openGreetingModal.bind(this));
        }
        if (this.submitGreeting) {
            this.submitGreeting.addEventListener('click', this.handleSubmitGreeting.bind(this));
        }

        // Invite management
        if (this.addInviteeBtn) {
            this.addInviteeBtn.addEventListener('click', this.openInviteModal.bind(this));
        }
        if (this.submitInvitee) {
            this.submitInvitee.addEventListener('click', this.handleSubmitInvitee.bind(this));
        }
        if (this.inviteList) {
            this.inviteList.addEventListener('click', this.onRevokeAccessClicked.bind(this));
        }
        if (this.removeAccessBtn) {
            this.removeAccessBtn.addEventListener('click', this.handleRemoveAccess.bind(this));
        }
        if (this.deletePageBtn) {
            this.deletePageBtn.addEventListener('click', this.handleDeletePage.bind(this));
        }
        if (this.deleteProject) {
            this.deleteProject.addEventListener('click', this.openDeleteModal.bind(this));
        }
        if (this.emailContainer) {
            this.emailContainer.addEventListener('click', this.handleEmailDelete.bind(this));
        }

        // Character count for description
        const projectDesc = document.getElementById('projectDesc');
        if (projectDesc) {
            projectDesc.addEventListener('input', () => {
                if (projectDesc.value.length > this.descriptionCharLimit) {
                    projectDesc.value = projectDesc.value.slice(0, this.descriptionCharLimit);
                }
                if (this.charCount) {
                    this.charCount.textContent = projectDesc.value.length;
                }
            });
        }

        // Fullscreen event
        document.addEventListener('fullscreenchange', this.onFullScreenToggle.bind(this));

        window.addEventListener('resize', () => {
            if (!this.pdfDocument) return;
            clearTimeout(this.resizeRenderTimeout);
            this.resizeRenderTimeout = setTimeout(() => {
                this.renderCurrentPage();
            }, 200);
        });

        // Hide fullscreen on mobile
        this.hideFullScreenOnMobile();
    }

    initializeModals() {
        this.closeAllModals();

        document.addEventListener('click', (event) => {
            const trigger = event.target.closest('[data-reveal-id]');
            if (trigger) {
                event.preventDefault();
                const modalId = trigger.getAttribute('data-reveal-id');
                if (modalId) {
                    this.openModal(modalId);
                }
                return;
            }

            if (event.target.closest('[data-close]')) {
                this.closeAllModals();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                this.closeAllModals();
            }
        });
    }

    openModal(modalId) {
        this.closeAllModals();
        const modal = document.getElementById(modalId);
        if (!modal) return;

        modal.classList.add('is-visible');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('modal-open');

        const firstFocusable = modal.querySelector('input, textarea, button, [href], [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            setTimeout(() => firstFocusable.focus(), 0);
        }
    }

    closeAllModals() {
        this.modals.forEach((modal) => {
            modal.classList.remove('is-visible');
            modal.setAttribute('aria-hidden', 'true');
        });
        document.body.classList.remove('modal-open');
    }

    openGreetingModal(event) {
        if (event) event.preventDefault();
        this.handleGreetingClick();
        this.openModal('ShareGreeting');
    }

    openInviteModal(event) {
        if (event) event.preventDefault();
        this.handleAddInvitee();
        this.openModal('ShareAdd');
    }

    openDeleteModal(event) {
        const trigger = event.target.closest('[data-reveal-id="ShareDelete"]');
        if (!trigger) return;
        event.preventDefault();
        this.openModal('ShareDelete');
    }

    hideFullScreenOnMobile() {
        const userAgent = navigator.userAgent || navigator.vendor || window.opera;
        const isIpad = /iPad|Macintosh/i.test(userAgent) && 'ontouchend' in document;
        if (this.isMobile || isIpad) {
            if (this.fullScreenButton) {
                this.fullScreenButton.style.display = 'none';
            }
        }
    }

    updateFullScreenButtonLabel() {
        if (!this.fullScreenButtonLabel) return;
        this.fullScreenButtonLabel.textContent = this.isFullScreen ? 'Exit Full Screen' : 'Full Screen';
    }

    getUrlParam(param) {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get(param);
    }

    objectToQueryParam(obj) {
        return Object.keys(obj)
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
            .join('&');
    }

    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }

    classifyError(error) {
        const status = Number((error && error.status) || 0);
        const code = String((error && error.code) || '');
        const message = String((error && error.message) || '').toLowerCase();

        if (status === 401 || code === 'AUTH_REQUIRED' || code === 'AUTH_SESSION_EXPIRED' || message.includes('not authenticated')) {
            return { kind: 'auth' };
        }

        if (status === 404 || message === '404') {
            return { kind: 'notFound' };
        }

        if (status === 403) {
            return { kind: 'forbidden' };
        }

        return { kind: 'unknown' };
    }

    isAuthFailure(error) {
        return this.classifyError(error).kind === 'auth';
    }

    handleAuthFailure(reason) {
        if (window.authState && typeof window.authState.handleAuthFailure === 'function') {
            window.authState.handleAuthFailure(reason || 'shared-project-detail-auth-failure');
            return;
        }

        if (typeof window.redirectToAuthInit === 'function') {
            window.redirectToAuthInit();
            return;
        }

        window.location.assign('/account/login');
    }

    async loadProject() {
        try {
            this.showLoader(true);
            this.hideAllErrors();

            if (!this.shareProjectId) {
                this.showError(this.projectNotAvailable);
                return;
            }

            // Try inviter endpoint first, fall back to invitee if not found
            let projects = await window.SharedProjectsService.getProjects({
                apiBaseUrl: this.apiBaseUrl,
                currency: this.currency,
                region: this.region,
                culture: this.culture,
                id: this.shareProjectId,
                endpoint: 'inviter',
                includeInvitees: true
            });

            // Filter to find our project
            let project = projects[0];//.find(p => p.shareProjectId === this.shareProjectId);
              if (projects.length === 0) {
                this.showError(this.projectNotAvailable);
                return;
            }
            // If not found as inviter, try invitee endpoint (use case-insensitive email comparison)
            // Determine if current user is the inviter
            // Compare against mimeoUserId from JWT, Shopify customerId, and email
            const projectUserId = String(project.userId || '');
            const currentMimeoId = String(this.mimeoUserId || '');
            const currentCustomerId = String(this.customerId || '');
            if (
                (currentMimeoId && projectUserId === currentMimeoId) ||
                (currentCustomerId && projectUserId === currentCustomerId)
            ) {
                this.isInviter = true;
            }


            this.project = project;

            // Validate invitee access if not the inviter
            if (!this.isInviter && project.invitees) {
                let invitee = project.invitees.find(
                    inv => inv.userEmail && this.customerEmail &&
                           inv.userEmail.toLowerCase() === this.customerEmail.toLowerCase()
                );
                if(!invitee) {
                    if (this.project.isPublicProject) {
                            // Add current user to invitees in background
                            await this.addCurrentUserAsInvitee();
                        } else if (!invitee) {
                        this.showError(this.projectNotAvailable);
                        return;
                    }
                }
            }

            // Show project interface
            this.initializeView();

            // Check PDF status
            if (project && !project.completed) {
                // PDF is still processing
                this.updateProjectDetails(project);
                this.updateGreeting(project);
                this.showPDFError(this.bookProcessing, true);
                return;
            }

            const pdfUrl = project.readersPdfUri || project.readerPdfUrl || project.readerPdfUri || project.pdfUrl;

            if (!pdfUrl) {
                // PDF creation failed
                this.updateProjectDetails(project);
                if (project.invitees) {
                    this.updateInvitees(project.invitees);
                }
                this.showPDFError(this.bookCreationError, true);
                return;
            }

            // Everything is ready - show the project
            this.updateProjectDetails(project);
            if (project.invitees) {
                this.updateInvitees(project.invitees);
            }
            this.updateGreeting(project);

            // Initialize PDF viewer
            this.showLoader(false);
            this.initializeWebViewer(pdfUrl);

        } catch (error) {
            console.error('Exception loading project:', error);

            const failure = this.classifyError(error);
            if (failure.kind === 'auth') {
                this.handleAuthFailure('shared-project-detail-load-auth-failure');
                return;
            }

            if (failure.kind === 'notFound') {
                this.showError(this.projectNotAvailable);
            } else {
                this.showError(this.serverError);
            }
        } finally {
            this.showLoader(false);
        }
    }

    initializeView() {
        if (this.projectHeader) {
            this.projectHeader.classList.remove('hidden');
        }
        if (this.isInviter) {
            this.editGreetingBtn.classList.remove("hidden");
        }
    }

    updateProjectDetails(project) {
        // Update heading
        if (this.heading) {
            this.heading.textContent = project.customGreetingsHeading || 'Share';
        }

        // Update product details
        if (this.productDetails) {
            const pageText = project.pageCount === 1 ? 'page' : 'pages';
            const safeProductName = this.escapeHtml(project.productName || '');
            const safePageCount = this.escapeHtml(String(project.pageCount || ''));
            this.productDetails.innerHTML = `<span>${safeProductName}</span><span class="notranslate"> - ${safePageCount}</span> <span>${pageText}</span>`;
        }

        // Update invitation details
        if (this.invitationDetails) {
            const date = new Intl.DateTimeFormat('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            }).format(new Date(project.projectUpdatedDate || project.projectShareDate));

            const safeCreatorName = this.escapeHtml(project.sharedProjectCreatorName || '');

            if (this.isInviter) {
                this.invitationDetails.innerHTML = `<span>Shared on:</span> <span>${date}</span>`;
            } else {
                this.invitationDetails.innerHTML = `<span>Shared by</span> <span>${safeCreatorName}</span><span> on</span> <span>${date}</span>`;
            }
        }

        // Update order button
        if (this.orderButton) {
            this.orderButton.innerHTML = `<span>Order </span><span>${project.currencyFormattedPrice}</span>`;
            this.orderButton.setAttribute('data-project-id', project.projectId);
        }

        // Show/hide stats and invite controls based on user role
        if (this.isInviter) {
            if (this.deleteProject) {
                this.deleteProject.classList.remove('hidden');
            }
            // Show edit buttons
            if (this.editGreetingBtn) this.editGreetingBtn.classList.remove('hidden');
            if (this.addGreetingBtnDesc) this.addGreetingBtnDesc.classList.remove('hidden');
            if (this.editGreetingBtnDesc) this.editGreetingBtnDesc.classList.remove('hidden');
        }

        // Update product type in all elements
        this.updateProductType(project.productType || 'Book');
    }

    updateGreeting(project) {
        if (!this.projectGreeting) return;

        this.projectGreeting.classList.remove('hidden');

        if (project.customGreetingDescription) {
            if (this.greetingDesc) {
                this.greetingDesc.textContent = project.customGreetingDescription;
            }
        } else {
            if (this.greetingDesc) {
                this.greetingDesc.textContent = '';
            }
        }

        // Show the combined edit/add button for inviters
        if (this.isInviter && this.addGreetingBtnDesc) {
            this.addGreetingBtnDesc.classList.add('hidden');
            this.editGreetingBtnDesc.classList.remove('hidden');
        }
    }

    updateInvitees(invitees) {
        if (!this.isInviter || !this.inviteList || !this.inviteTable) return;

        this.inviteList.classList.remove('hidden');

        const tableHeader = `
          <thead><tr><th class="head head1">Contact Email</th>
                    <th class="head">Purchases</th>
                    <th class="head">Remove</th></tr></thead>
        `;

        // Get icon URL from config (passed from Liquid) for better maintainability
        const iconDoNotDisturbUrl = window.shareProjectDetailConfig?.iconDoNotDisturbUrl || '';

        const tableRows = invitees.map(invitee => {
            const safeEmail = this.escapeHtml(invitee.userEmail || '');
            const safeOrders = this.escapeHtml(String(invitee.totalInviteeOrders ?? 0));
            return `
                <tr>
                    <td class="cell cell1">${safeEmail}</td>
                    <td class="cell">${safeOrders}</td>
                    <td class="cell">
                        <button type="button" class="button-link" data-reveal-id="ShareRemoveAccess" aria-label="Remove Access">
                            <span data-email="${safeEmail}" class="svg-wrapper error">${window.themeIcons?.doNotDisturbOn || ''}</span>
                        </button>
                    </td>
                </tr>
            `;
        }).join('');

        this.inviteTable.innerHTML = tableHeader + tableRows;
    }

    updateProductType(productType) {
        const elements = document.querySelectorAll('.productType');
        elements.forEach(ele => {
            const text = ele.textContent;
            if (productType === 'Calendar') {
                if (text.charAt(0) === 'B' && text.includes("'s")) {
                    ele.textContent = "Calendar's";
                } else if (text.charAt(0) === 'B') {
                    ele.textContent = "Calendar";
                } else if (text.includes("'s")) {
                    ele.textContent = "calendar's";
                } else {
                    ele.textContent = "calendar";
                }
            }
        });
    }

    // PDF Viewer Methods
    async initializeWebViewer(pdfUrl) {
        if (!this.viewer || !pdfUrl) return;

        if (this.projectViewer) {
            this.projectViewer.classList.remove('hidden');
        }

        const scriptLoaded = await this.ensurePdfJsLoaded();
        if (!scriptLoaded || !window.pdfjsLib) {
            console.error('PDF.js library failed to load');
            this.showPDFLoader(false);
            this.showPDFError(this.bookCreationError, false);
            return;
        }

        try {
            this.showPDFLoader(true);
            this.clearPdfLoadTimeout();
            if (this.pageIndicator) this.pageIndicator.style.display = 'none';
            if (this.topHeaderControls) this.topHeaderControls.style.display = 'none';

            this.pdfLoadTimeout = setTimeout(() => {
                console.error('PDF load timed out before documentLoaded event');
                this.onDocumentLoadFailed(new Error('PDF load timeout'));
            }, 15000);

            const docConfig = this.getViewerDocumentConfig(pdfUrl);
            await this.loadPdfDocument(docConfig);

        } catch (error) {
            console.error('Error initializing PDF viewer:', error);
            this.onDocumentLoadFailed(error);
        }
    }

    setupPdfCanvasContainer() {
        if (!this.viewer) return null;
        if (!this.pdfCanvasContainer) {
            this.viewer.innerHTML = '';
            this.pdfCanvasContainer = document.createElement('div');
            this.pdfCanvasContainer.className = 'pdf-canvas-container';
            this.pdfCanvasContainer.style.display = 'flex';
            this.pdfCanvasContainer.style.alignItems = 'center';
            this.pdfCanvasContainer.style.justifyContent = 'center';
            this.pdfCanvasContainer.style.width = '100%';
            this.pdfCanvasContainer.style.height = '100%';
            this.pdfCanvasContainer.style.padding = '8px';
            this.pdfCanvasContainer.style.boxSizing = 'border-box';
            this.viewer.appendChild(this.pdfCanvasContainer);
        }

        this.pdfCanvasContainer.classList.toggle('is-facing-cover', this.pdfLayoutMode === 'facing-cover');

        const showSpread = this.pdfLayoutMode === 'facing-cover' && this.currentPageNumber > 1;
        this.pdfCanvasContainer.style.gap = showSpread ? '0' : '0';
        return this.pdfCanvasContainer;
    }

    cancelActiveRenderTasks() {
        if (!Array.isArray(this.activeRenderTasks)) return;
        this.activeRenderTasks.forEach((task) => {
            if (task && typeof task.cancel === 'function') {
                task.cancel();
            }
        });
        this.activeRenderTasks = [];
    }

    normalizeFacingPage(pageNumber) {
        if (pageNumber <= 1) return 1;
        return pageNumber % 2 === 0 ? pageNumber : pageNumber - 1;
    }

    setPDFLayout(preserveCurrentPage = false) {
        const type = this.project && this.project.productSubType ? this.project.productSubType.toLowerCase() : '';
        const maxPage = this.pdfDocument ? this.pdfDocument.numPages : 1;
        const safeCurrentPage = Math.min(Math.max(this.currentPageNumber || 1, 1), maxPage);

        if (type === 'calendar' && !this.isFullScreen) {
            this.pdfLayoutMode = 'single';
            this.currentPageNumber = preserveCurrentPage ? safeCurrentPage : 1;
            this.pageCounter = this.currentPageNumber;
            return;
        }

        if (type === 'calendar' && this.isFullScreen) {
            this.pdfLayoutMode = 'continuous';
            this.currentPageNumber = preserveCurrentPage ? safeCurrentPage : 1;
            this.pageCounter = this.currentPageNumber;
            return;
        }

        if (type === 'continuous') {
            this.pdfLayoutMode = 'continuous';
            this.currentPageNumber = preserveCurrentPage ? safeCurrentPage : 1;
            this.pageCounter = this.currentPageNumber;
            return;
        }

        this.pdfLayoutMode = 'facing-cover';
        if (!preserveCurrentPage) {
            this.currentPageNumber = 1;
            this.pageCounter = 1;
            return;
        }

        if (safeCurrentPage <= 1) {
            this.currentPageNumber = 1;
            this.pageCounter = 1;
        } else {
            this.currentPageNumber = this.normalizeFacingPage(safeCurrentPage);
            this.pageCounter = this.currentPageNumber;
        }
    }

    getCurrentViewPages() {
        if (!this.pdfDocument) {
            return {
                left: null,
                right: null,
                counter: this.currentPageNumber,
            };
        }

        if (this.pdfLayoutMode !== 'facing-cover') {
            return {
                left: this.currentPageNumber,
                right: null,
                counter: this.currentPageNumber,
            };
        }

        if (this.currentPageNumber <= 1) {
            return {
                left: null,
                right: 1,
                counter: 1,
            };
        }

        const leftPage = this.normalizeFacingPage(this.currentPageNumber);
        const rightPage = leftPage + 1;

        if (rightPage <= this.pdfDocument.numPages) {
            return {
                left: leftPage,
                right: rightPage,
                counter: leftPage,
            };
        }

        return {
            left: leftPage,
            right: null,
            counter: leftPage,
        };
    }

    clearPdfLoadTimeout() {
        if (this.pdfLoadTimeout) {
            clearTimeout(this.pdfLoadTimeout);
            this.pdfLoadTimeout = null;
        }
    }

    resolvePdfUrl(pdfUrl) {
        if (!pdfUrl || typeof pdfUrl !== 'string') {
            return pdfUrl;
        }

        if (/^(blob:|data:|https?:\/\/)/i.test(pdfUrl)) {
            return pdfUrl;
        }

        if (pdfUrl.startsWith('//')) {
            return `https:${pdfUrl}`;
        }

        const normalizedBase = (this.apiBaseUrl || '').replace(/\/$/, '');
        const normalizedPath = pdfUrl.startsWith('/') ? pdfUrl : `/${pdfUrl}`;
        return `${normalizedBase}${normalizedPath}`;
    }

    isSasBlobUrl(url) {
        if (!url || typeof url !== 'string') {
            return false;
        }

        try {
            const parsed = new URL(url, window.location.origin);
            return parsed.searchParams.has('sig') && parsed.searchParams.has('sv');
        } catch (_error) {
            return /[?&]sig=/.test(url) && /[?&]sv=/.test(url);
        }
    }

    /**
     * Build a document config for PDF.js instead of downloading the entire file.
     * Azure Blob Storage (SAS URLs) and many API endpoints support HTTP Range
     * requests, so we let PDF.js fetch only the bytes it needs for each page.
     *
     * @returns {{ url: string, httpHeaders: Object, useRangeRequests: boolean }}
     */
    getViewerDocumentConfig(pdfUrl) {
        const resolvedUrl = this.resolvePdfUrl(pdfUrl);
        // Use authState for token access with fallback to localStorage
        const authToken = window.authState?.getTokenSync() || localStorage.getItem('auth_token');
        const isSasUrl = this.isSasBlobUrl(resolvedUrl);

        if (!resolvedUrl || /^(blob:|data:)/i.test(resolvedUrl)) {
            return { url: resolvedUrl, httpHeaders: {}, useRangeRequests: false };
        }

        // Azure Blob Storage SAS URLs natively support Range requests
        if (isSasUrl) {
            return { url: resolvedUrl, httpHeaders: {}, useRangeRequests: true };
        }

        if (!authToken) {
            return { url: resolvedUrl, httpHeaders: {}, useRangeRequests: false };
        }

        // Authenticated API endpoint – pass headers to PDF.js so it can make
        // range requests on our behalf without downloading the whole file first
        return {
            url: resolvedUrl,
            httpHeaders: {
                'Authorization': `Bearer ${authToken}`,
                'api-version': '2.0'
            },
            useRangeRequests: true
        };
    }

    async ensurePdfJsLoaded() {
        if (window.pdfjsLib) {
            return true;
        }

        if (!SharedProjectDetail.pdfJsLoadPromise) {
            SharedProjectDetail.pdfJsLoadPromise = new Promise((resolve) => {
                const script = document.createElement('script');
                script.src = this.pdfJsScriptUrl;
                script.async = true;
                script.onload = () => {
                    if (window.pdfjsLib && window.pdfjsLib.GlobalWorkerOptions) {
                        window.pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfJsWorkerUrl;
                    }
                    resolve(!!window.pdfjsLib);
                };
                script.onerror = () => resolve(false);
                document.head.appendChild(script);
            });
        }

        return SharedProjectDetail.pdfJsLoadPromise;
    }

    /**
     * Load the PDF using range / chunked requests when the server supports it.
     * PDF.js will issue HTTP Range requests so only the bytes needed for the
     * current page are downloaded first, making the initial render much faster
     * for large files hosted on Azure Blob Storage.
     *
     * If the range-request approach fails (e.g. CORS blocks custom headers),
     * we fall back to downloading the full file as a blob.
     */
    async loadPdfDocument(docConfig) {
        if (!window.pdfjsLib || !docConfig || !docConfig.url) {
            throw new Error('PDF.js is not available or document URL is missing');
        }

        const loadOptions = {
            url: docConfig.url,
            withCredentials: false,
        };

        // Forward auth / custom headers so PDF.js can attach them to every
        // range request it makes.
        if (docConfig.httpHeaders && Object.keys(docConfig.httpHeaders).length > 0) {
            loadOptions.httpHeaders = docConfig.httpHeaders;
        }

        // Enable chunked / on-demand fetching.  PDF.js will use HTTP Range
        // headers when the server responds with Accept-Ranges: bytes.
        //   • rangeChunkSize  – size of each byte-range request (128 KB)
        //   • disableAutoFetch – don't pre-fetch pages that aren't visible yet
        //   • disableStream   – false → allow streaming the initial request
        if (docConfig.useRangeRequests) {
            loadOptions.rangeChunkSize = 128 * 1024; // 128 KB per chunk
            loadOptions.disableAutoFetch = true;
            loadOptions.disableStream = false;
        }

        const loadingTask = window.pdfjsLib.getDocument(loadOptions);

        try {
            this.pdfDocument = await loadingTask.promise;
        } catch (error) {
            // If range-request loading failed and we used custom headers,
            // fall back to downloading the entire file as a blob.
            if (docConfig.useRangeRequests &&
                docConfig.httpHeaders &&
                Object.keys(docConfig.httpHeaders).length > 0) {
                console.warn('Range-request loading failed, falling back to full download:', error);
                return this.loadPdfDocumentFallback(docConfig);
            }
            throw error;
        }

        await this.finalizePdfDocument();
    }

    /**
     * Fallback: download the full PDF with auth headers, create a local blob
     * URL, and hand it to PDF.js.  Used when range requests are blocked.
     */
    async loadPdfDocumentFallback(docConfig) {
        const response = await fetch(docConfig.url, {
            headers: docConfig.httpHeaders || {}
        });

        if (!response.ok) {
            throw new Error(`Failed to download PDF: ${response.status}`);
        }

        const pdfBlob = await response.blob();
        if (this.viewerBlobUrl) {
            URL.revokeObjectURL(this.viewerBlobUrl);
        }
        this.viewerBlobUrl = URL.createObjectURL(pdfBlob);

        const loadingTask = window.pdfjsLib.getDocument({
            url: this.viewerBlobUrl,
            withCredentials: false,
        });

        this.pdfDocument = await loadingTask.promise;
        await this.finalizePdfDocument();
    }

    /**
     * Shared post-load initialisation: read base page size, set layout, render.
     */
    async finalizePdfDocument() {
        const basePage = await this.pdfDocument.getPage(1);
        const baseViewport = basePage.getViewport({ scale: 1 });
        this.facingBasePageSize = {
            width: baseViewport.width,
            height: baseViewport.height,
        };
        this.currentPageNumber = 1;
        this.setPDFLayout();
        this.onDocumentLoaded();
        await this.renderCurrentPage();
    }

    createPdfPageSlot(isSingleSlot) {
        const slot = document.createElement('div');
        slot.className = isSingleSlot ? 'pdf-page-slot pdf-page-slot--single' : 'pdf-page-slot';
        return slot;
    }

    async renderPageToSlot(pageNumber, slot, targetWidth, targetHeight, useFacingBaseSize, drawSpine) {
        const page = await this.pdfDocument.getPage(pageNumber);
        const pageViewport = page.getViewport({ scale: 1 });

        const referenceWidth = useFacingBaseSize && this.facingBasePageSize ? this.facingBasePageSize.width : pageViewport.width;
        const referenceHeight = useFacingBaseSize && this.facingBasePageSize ? this.facingBasePageSize.height : pageViewport.height;

        const widthScale = targetWidth > 0 ? targetWidth / referenceWidth : 1;
        const heightScale = targetHeight > 0 ? targetHeight / referenceHeight : 1;
        const scale = Math.max(0.1, Math.min(widthScale, heightScale));
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-canvas';
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        canvas.style.maxWidth = '100%';
        canvas.style.maxHeight = '100%';
        canvas.style.display = 'block';
        canvas.style.backgroundColor = '#fff';
        canvas.style.boxShadow = '0 0 2px 0 rgba(0, 0, 0, .14), 0 2px 2px 0 rgba(0, 0, 0, .12), 0 1px 3px 0 rgba(0, 0, 0, .12)';
        canvas.style.borderRight = drawSpine ? '1px solid rgba(0, 0, 0, 0.28)' : 'none';
        slot.appendChild(canvas);

        const task = page.render({
            canvasContext: canvas.getContext('2d'),
            viewport,
        });
        this.activeRenderTasks.push(task);
        await task.promise;
    }

    async renderCurrentPage() {
        if (!this.pdfDocument) return;

        const container = this.setupPdfCanvasContainer();
        if (!container) return;

        this.cancelActiveRenderTasks();
        container.innerHTML = '';

        const viewPages = this.getCurrentViewPages();
        const availableWidth = container.clientWidth || (this.viewer ? this.viewer.clientWidth : 0);
        const availableHeight = container.clientHeight || (this.viewer ? this.viewer.clientHeight : 0);

        const renderPromises = [];
        const hasFacingSpread = this.pdfLayoutMode === 'facing-cover' && !!viewPages.left && !!viewPages.right;

        if (this.pdfLayoutMode === 'facing-cover') {
            const leftSlot = this.createPdfPageSlot(false);
            leftSlot.classList.add('pdf-page-slot--left');
            const rightSlot = this.createPdfPageSlot(false);
            rightSlot.classList.add('pdf-page-slot--right');
            container.appendChild(leftSlot);
            container.appendChild(rightSlot);

            const slotWidth = Math.max(120, Math.floor(availableWidth / 2));
            const slotHeight = availableHeight;

            if (viewPages.left) {
                renderPromises.push(this.renderPageToSlot(viewPages.left, leftSlot, slotWidth, slotHeight, true, hasFacingSpread));
            }
            if (viewPages.right) {
                renderPromises.push(this.renderPageToSlot(viewPages.right, rightSlot, slotWidth, slotHeight, true, false));
            }
        } else {
            const singleSlot = this.createPdfPageSlot(true);
            container.appendChild(singleSlot);
            if (viewPages.left) {
                renderPromises.push(this.renderPageToSlot(viewPages.left, singleSlot, availableWidth, availableHeight, false, false));
            }
        }

        try {
            await Promise.all(renderPromises);
            this.updatePageNumber(viewPages.counter || this.pageCounter || this.currentPageNumber);
        } catch (error) {
            if (error && error.name !== 'RenderingCancelledException') {
                throw error;
            }
        }
    }

    onDocumentLoaded() {
        this.clearPdfLoadTimeout();

        // Update page count
        if (this.totalPages && this.pdfDocument) {
            this.totalPages.textContent = this.pdfDocument.numPages;
        }
        if (this.pageIndicator) {
            this.pageIndicator.style.display = 'flex';
        }
        if (this.topHeaderControls) {
            this.topHeaderControls.style.display = 'flex';
        }

        setTimeout(() => {
            this.showPDFLoader(false);
        }, 300);
    }

    onDocumentLoadFailed(error) {
        this.clearPdfLoadTimeout();
        console.error('PDF document failed to load:', error);
        this.showPDFLoader(false);
        this.showPDFError(this.bookCreationError, false);
    }

    updatePageNumber(pageNumber) {
        if (this.pageNumberSpan) {
            this.pageNumberSpan.textContent = pageNumber;
        }
    }

    navigateToNextPage() {
        if (!this.pdfDocument) return;

        if (this.pdfLayoutMode === 'facing-cover') {
            if (this.currentPageNumber <= 1 && this.pdfDocument.numPages > 1) {
                this.currentPageNumber = 2;
            } else if (this.currentPageNumber < this.pdfDocument.numPages - 1) {
                this.currentPageNumber = this.normalizeFacingPage(this.currentPageNumber + 2);
            }
            this.pageCounter = this.currentPageNumber <= 1 ? 1 : this.currentPageNumber;
        } else if (this.currentPageNumber < this.pdfDocument.numPages) {
            this.currentPageNumber += 1;
            this.pageCounter = this.currentPageNumber;
        }

        this.renderCurrentPage().catch((error) => {
            this.onDocumentLoadFailed(error);
        });
    }

    navigateToPreviousPage() {
        if (!this.pdfDocument) return;

        if (this.pdfLayoutMode === 'facing-cover') {
            if (this.currentPageNumber > 2) {
                this.currentPageNumber = this.normalizeFacingPage(this.currentPageNumber - 2);
            } else {
                this.currentPageNumber = 1;
            }
            this.pageCounter = this.currentPageNumber <= 1 ? 1 : this.currentPageNumber;
        } else if (this.currentPageNumber > 1) {
            this.currentPageNumber -= 1;
            this.pageCounter = this.currentPageNumber;
        }

        this.renderCurrentPage().catch((error) => {
            this.onDocumentLoadFailed(error);
        });
    }

    handleFullScreen() {
        if (!this.isFullScreen) {
            if (this.viewerContainer.requestFullscreen) {
                this.viewerContainer.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            }
        }
    }

    onFullScreenToggle() {
        this.isFullScreen = !!document.fullscreenElement;
        this.updateFullScreenButtonLabel();
        if (this.pdfDocument) {
            this.setPDFLayout(true);
            setTimeout(() => {
                this.renderCurrentPage().catch(() => {
                    // ignore repaint issues on fullscreen transitions
                });
            }, 120);
        }
    }

    // Order handling
    async handleCreateOrder() {
        if (!this.project) return;

        try {
            this.showLoader(true);
            const checkoutOrigin = this.isInviter ? 'shared-by-me' : 'shared-with-me';
            await window.SharedProjectsService.createOrder(this.project, {
                checkoutOrigin: checkoutOrigin
            });
        } catch (error) {
            if (this.isAuthFailure(error)) {
                this.handleAuthFailure('shared-project-detail-create-order-auth-failure');
                return;
            }
            console.error('Error creating order:', error);
            this.showLoader(false);
            alert('Failed to add to cart. Please try again.');
        }
    }

    // Greeting management
    handleGreetingClick() {
        const projectHeading = document.getElementById('projectHeading');
        const projectDesc = document.getElementById('projectDesc');

        if (projectHeading && this.project) {
            projectHeading.value = this.project.customGreetingsHeading || '';
        }
        if (projectDesc && this.project) {
            projectDesc.value = this.project.customGreetingDescription || '';
            if (this.charCount) {
                this.charCount.textContent = projectDesc.value.length;
            }
        }
    }

    async handleSubmitGreeting() {
        const projectHeading = document.getElementById('projectHeading');
        const projectDesc = document.getElementById('projectDesc');

        if (!projectHeading || !this.project) return;

        const heading = projectHeading.value.trim();
        const description = projectDesc ? projectDesc.value.trim() : '';

        if (!heading) {
            alert('Heading is required');
            return;
        }

        if (description.length > this.descriptionCharLimit) {
            alert(`Description must be ${this.descriptionCharLimit} characters or fewer`);
            return;
        }

        try {
             const reqObj = {
                projectId: this.project.projectId,
                shareProjectId: this.project.shareProjectId,
                customGreetingsHeading: heading,
                customGreetingDescription: description,
            };
            // Use authenticatedFetch for automatic token refresh and 401 retry
            const response = await window.authState.authenticatedFetch(
                `${this.apiBaseUrl}/share/projects/greeting`,
                {
                    method: "PUT",
                    headers: {
                        "api-version": "2.0",
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(reqObj),
                }
            );
            if (!response.ok) {
                throw new Error(response.status);
            }

            // Update local project
            this.project.customGreetingsHeading = heading;
            this.project.customGreetingDescription = description;

            // Update UI
            this.updateProjectDetails(this.project);
            this.updateGreeting(this.project);
            this.closeAllModals();

        } catch (error) {
            if (this.isAuthFailure(error)) {
                this.handleAuthFailure('shared-project-detail-greeting-auth-failure');
                return;
            }
            console.error('Error updating greeting:', error);
            alert('Failed to update greeting. Please try again.');
        }
    }

    // Invitee management
    handleAddInvitee() {
        if (!this.emailContainer) return;

        this.resetEmails();
        this.renderEmails();
        this.detachEmailInputHandlers();

        // Prevent Enter from inserting a newline in the contenteditable span
        this.onEmailKeyDown = (event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
            }
        };

        this.onEmailKeyUp = (event) => {
            const keyCode = event.key;
            let email = event.target.textContent;
            email = email.trim();
            if (email.endsWith(',') || keyCode === 'Enter') {
                event.target.textContent = '';
                if (email.endsWith(',')) email = email.slice(0, -1);
                email = email.replace(/^"(.+(?="$))"$/, '$1');
                if (!email) return;
                email = email.toLowerCase();
                if (this.isDuplicateEmail(email)) return;
                const isValid = this.validateEmail(email);
                this.inviteeList.push({
                    userEmail: email,
                    isValid: isValid,
                    readOnly: false,
                });
                this.renderEmails();
                document.getElementById('emailInput')?.focus();
            }
        };

        this.onEmailPaste = (event) => {
            event.preventDefault();
            let pastedText = (event.clipboardData || window.clipboardData).getData('text');
            const emails = pastedText.split(',');
            emails.forEach((email) => {
                email = email.trim();
                email = email.replace(/^"(.+(?="$))"$/, '$1');
                if (!email) return;
                if (this.isDuplicateEmail(email)) return;
                const isValid = this.validateEmail(email);
                this.inviteeList.push({
                    userEmail: email,
                    isValid: isValid,
                    readOnly: false,
                });
            });
            this.renderEmails();
            document.getElementById('emailInput')?.focus();
            event.target.textContent = '';
        };

        this.emailContainer.addEventListener('keydown', this.onEmailKeyDown);
        this.emailContainer.addEventListener('keyup', this.onEmailKeyUp);
        this.emailContainer.addEventListener('paste', this.onEmailPaste);

        setTimeout(() => {
            document.getElementById('emailInput')?.focus();
        }, 300);
    }

    detachEmailInputHandlers() {
        if (!this.emailContainer) return;

        if (this.onEmailKeyDown) {
            this.emailContainer.removeEventListener('keydown', this.onEmailKeyDown);
        }
        if (this.onEmailKeyUp) {
            this.emailContainer.removeEventListener('keyup', this.onEmailKeyUp);
        }
        if (this.onEmailPaste) {
            this.emailContainer.removeEventListener('paste', this.onEmailPaste);
        }
    }

    validateEmail(email) {
        return this.emailRegex.test(email);
    }

    resetEmails() {
        this.inviteeList = [];
    }

    isDuplicateEmail(email) {
        return this.inviteeList.some((invitee) => invitee.userEmail === email);
    }

    handleEmailDelete(event) {
        const userEmail = event.target.dataset.userEmail;
        if (userEmail) {
            this.inviteeList = this.inviteeList.filter(
                (email) => email.userEmail !== userEmail
            );
            this.renderEmails();
        }
        document.getElementById('emailInput')?.focus();
    }

    renderEmails() {
        const submitInviteeBtn = document.getElementById('submitInvitee');
        const emailList = this.inviteeList.map(
            (email, index) =>
                `<div class="toggle-button">
                    <div class="label ${email.isValid ? 'primary-color' : 'error-msg'} ${email.readOnly ? 'read-only' : ''}">${this.escapeHtml(email.userEmail)}&nbsp${email.readOnly ? '' : `<span class="svg-wrapper close-icon" id="removeEmailBtn-${index}" data-user-email="${this.escapeHtml(email.userEmail)}">${window.themeIcons?.closeSmall || ''}</span>`}
                    </div>
                </div>`
        );
        emailList.push(
            '<span class="email-input" contenteditable="true" autocapitalize="off" spellcheck="false" id="emailInput"></span>'
        );

        const inputAddInvitee = document.getElementById('inputAddInvitee');
        if (inputAddInvitee) {
            inputAddInvitee.innerHTML = emailList.join('');
        }

        if (this.emailCount) {
            this.emailCount.textContent = this.inviteeList.length;
        }

        const hasInvalidEmail = this.inviteeList.some(email => !email.isValid);
        const exceedsLimit = this.inviteeList.length > this.emailLimitValue;

        if (submitInviteeBtn) {
            submitInviteeBtn.disabled = hasInvalidEmail || exceedsLimit || this.inviteeList.length === 0;
        }
    }

    async addCurrentUserAsInvitee() {
        try {
            this.showLoader(true);
             this.inviteeList.push({
                    userEmail:  this.customerEmail,
                    isValid: true,
                    readOnly: false,
                });
            await this.handleSubmitInvitee();
            this.showLoader(false);
        } catch (error) {
            // throw 404 project not available error to handle in loadSharedProject
            console.error('Failed to add current user as invitee:', error);
            this.showLoader(false);
            if (this.isAuthFailure(error)) {
                throw error;
            }
            const inviteeError = new Error('404');
            inviteeError.status = 404;
            throw inviteeError;
        }
    }

    async handleSubmitInvitee() {
        if (this.inviteeList.length === 0 || !this.project) return;

        const validEmails = this.inviteeList.filter(e => e.isValid).map(e => e.userEmail);

        try {
            // Use authenticatedFetch for automatic token refresh and 401 retry
            const response = await window.authState.authenticatedFetch(
                `${this.apiBaseUrl}/share/projects/${this.project.projectId}/${this.project.shareProjectId}/invitee`,
                {
                    method: 'PUT',
                    headers: {
                        'api-version': '2.0',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ invitees: validEmails })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to add invitees');
            }

            // Reload project to get updated invitees
            await this.loadProject();
            this.closeAllModals();

        } catch (error) {
            if (this.isAuthFailure(error)) {
                this.handleAuthFailure('shared-project-detail-add-invitee-auth-failure');
                return;
            }
            console.error('Error adding invitees:', error);
            alert('Failed to add invitees. Please try again.');
        }
    }

    onRevokeAccessClicked(event) {
        const email = event.target.closest('[data-email]')?.dataset.email;
        if (email) {
            const emailLabel = document.getElementById('emailLabel');
            if (emailLabel) {
                emailLabel.textContent = email;
            }
            this.selectedInviteeEmail = email;
            this.openModal('ShareRemoveAccess');
        }
    }

    async handleRemoveAccess() {
        if (!this.selectedInviteeEmail || !this.project) return;

        try {
            // Use authenticatedFetch for automatic token refresh and 401 retry
            const response = await window.authState.authenticatedFetch(
                `${this.apiBaseUrl}/share/projects/${this.project.shareProjectId}/invitees/${encodeURIComponent(this.selectedInviteeEmail)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'api-version': '2.0'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to remove access');
            }

            // Reload project to get updated invitees
            await this.loadProject();
            this.closeAllModals();

        } catch (error) {
            if (this.isAuthFailure(error)) {
                this.handleAuthFailure('shared-project-detail-remove-access-auth-failure');
                return;
            }
            console.error('Error removing access:', error);
            alert('Failed to remove access. Please try again.');
        }
    }

    async handleDeletePage() {
        if (!this.project) return;

        try {
            const reqObj = {
                projectId: this.project.projectId,
                shareProjectId: this.project.shareProjectId,
            };

            // Use authenticatedFetch for automatic token refresh and 401 retry
            const response = await window.authState.authenticatedFetch(
                `${this.apiBaseUrl}/share/projects/${this.project.projectId}/${this.project.shareProjectId}/delete`,
                {
                    method: "PUT",
                    headers: {
                        'api-version': '2.0'
                    },
                    body: JSON.stringify(reqObj),
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete project');
            }

            // Redirect to shared projects list, preserving the active locale prefix
            const rootPath = (window.shopifyContext && window.shopifyContext.rootUrl) || '';
            const localePrefix = (rootPath && rootPath !== '/') ? rootPath : '';
            window.location.href = localePrefix + '/pages/shared-projects-list';

        } catch (error) {
            if (this.isAuthFailure(error)) {
                this.handleAuthFailure('shared-project-detail-delete-auth-failure');
                return;
            }
            console.error('Error deleting project:', error);
            alert('Failed to delete project. Please try again.');
        }
    }

    // UI Helper Methods
    showLoader(show) {
        if (this.loader) {
            if (show) {
                this.loader.classList.remove('hidden');
            } else {
                this.loader.classList.add('hidden');
            }
        }
    }

    showPDFLoader(show) {
        if (this.pdfLoader) {
            if (show) {
                this.pdfLoader.classList.remove('hidden');
            } else {
                this.pdfLoader.classList.add('hidden');
            }
        }
    }

    showError(errorElement) {
        this.hideAllErrors();
        this.showLoader(false);
        if (this.projectHeader) this.projectHeader.classList.add('hidden');
        if (this.projectGreeting) this.projectGreeting.classList.add('hidden');
        if (this.projectViewer) this.projectViewer.classList.add('hidden');
        if (this.inviteList) this.inviteList.classList.add('hidden');
        if (this.deleteProject) this.deleteProject.classList.add('hidden');
        if (errorElement) {
            errorElement.classList.remove('hidden');
        }
    }

    showPDFError(errorElement, hideLoader) {
        if (hideLoader) {
            this.showLoader(false);
        }
        if (errorElement) {
            errorElement.classList.remove('hidden');
        }
    }

    hideAllErrors() {
        const errors = [this.projectNotAvailable, this.serverError, this.bookProcessing, this.bookCreationError];
        errors.forEach(el => {
            if (el) {
                el.classList.add('hidden');
            }
        });
    }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        new SharedProjectDetail();
    });
} else {
    new SharedProjectDetail();
}
